// Story 7-FU-F (ADO-1219) — the session depot seam (was Story 7.3's open
// question 1).
//
// Story 7.3 read a single deploy-time default (`VITE_SESSION_DEPOT_ID`) here and
// framed this module as "the seam the depot-picker story replaces". That story
// is 7-FU-F: the header `DepotPicker` now writes the user's selection into
// `useSessionStore.activeDepot` (sourced from the server session via
// `GET /api/v1/membership/tenant-context`, not a token claim), and this
// module resolves the session depot from THAT — retiring the env as the
// source of record (D-2; env deleted, not kept as a fallback — OQ-3).
//
// It stays the SINGLE UUID-guarded choke-point so availability still DEGRADES
// GRACEFULLY: when there is no active depot (unauthenticated / no `depotIds`, or
// a malformed value) it returns `null` and the availability features do nothing
// — no batch fetch, no socket, no badges (unknown is never claimed as In Stock).

import { useSessionStore } from '../auth/useSessionStore';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The session depot id, or `null` when there is no valid active depot
 * (availability disabled). Reads `useSessionStore.activeDepot` by default;
 * callers that already subscribe to the store (e.g. `useAvailability`, so its
 * effect re-runs on a switch) pass the subscribed value in so the UUID guard
 * and the null-degrade contract stay in this one place.
 */
export function resolveSessionDepotId(
  candidate: string | null = useSessionStore.getState().activeDepot,
): string | null {
  if (typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  return UUID_RE.test(trimmed) ? trimmed : null;
}

/**
 * The session depot id as a SUBSCRIBED value: the component re-renders, and any effect
 * declaring it re-runs, on a live switch. Every React caller wants this rather than the
 * bare `resolveSessionDepotId()`, whose `getState()` default is a point-in-time read that
 * never re-renders anything.
 *
 * A switch is NOT always accompanied by a store reset: `DepotPicker.handleChange` resets the
 * depot-scoped caches before flipping, but `useHeaderChrome` wires `onSelectDepot` straight
 * to `setActiveDepot` (and session hydration seeds `activeDepot` from `depotIds[0]`), so
 * those paths move the depot underneath caches that still hold the old one's data. That is
 * what `atDepot` (depot-scoped-entry.ts) exists to make unreadable.
 */
export function useSessionDepotId(): string | null {
  const activeDepot = useSessionStore((s) => s.activeDepot);
  return resolveSessionDepotId(activeDepot);
}
